import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

export type PdfViewPersist = {
  scale: number
  scrollLeft: number
  scrollTop: number
}

export const DEFAULT_PDF_VIEW: PdfViewPersist = { scale: 1, scrollLeft: 0, scrollTop: 0 }

interface EvidencePdfPaneProps {
  url: string
  view: PdfViewPersist
  onViewChange: (next: Partial<PdfViewPersist>) => void
}

export function EvidencePdfPane({ url, view, onViewChange }: EvidencePdfPaneProps) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const ignoreScrollRef = useRef(false)
  const appliedScroll = useRef({ left: view.scrollLeft, top: view.scrollTop })
  const urlRef = useRef(url)
  if (urlRef.current !== url) {
    urlRef.current = url
    appliedScroll.current = { left: view.scrollLeft, top: view.scrollTop }
  }
  const [pageWidth, setPageWidth] = useState(0)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const update = () => setPageWidth(el.clientWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const restore = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    ignoreScrollRef.current = true
    el.scrollLeft = appliedScroll.current.left
    el.scrollTop = appliedScroll.current.top
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ignoreScrollRef.current = false
      })
    })
  }, [])

  return (
    <div ref={wrapRef} className="flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollerRef}
        className="flex-1 min-h-0 overflow-auto bg-gray-100"
        data-testid="pdf-scroller"
        onScroll={(e) => {
          if (ignoreScrollRef.current) return
          const t = e.currentTarget
          const wantedLeft = appliedScroll.current.left
          const wantedTop = appliedScroll.current.top
          const topClamped = wantedTop > t.scrollTop && t.scrollHeight <= t.clientHeight + 1
          const leftClamped = wantedLeft > t.scrollLeft && t.scrollWidth <= t.clientWidth + 1
          const left = leftClamped ? wantedLeft : t.scrollLeft
          const top = topClamped ? wantedTop : t.scrollTop
          if (left === wantedLeft && top === wantedTop) return
          appliedScroll.current = { left, top }
          onViewChange({ scrollLeft: left, scrollTop: top })
        }}
        onWheel={(e) => {
          if (!e.ctrlKey && !e.metaKey) return
          e.preventDefault()
          const delta = e.deltaY < 0 ? 0.1 : -0.1
          const next = Math.min(3, Math.max(0.4, Math.round((view.scale + delta) * 10) / 10))
          onViewChange({ scale: next })
        }}
      >
        <Document file={url} onLoadSuccess={restore} loading={<p className="text-sm text-gray-500 p-4">Lade PDF…</p>}>
          {pageWidth > 0 ? (
            <Page
              pageNumber={1}
              width={Math.max(1, Math.floor(pageWidth * view.scale))}
              onRenderSuccess={restore}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ) : null}
        </Document>
      </div>
    </div>
  )
}
